import {
  Alert,
  Anchor,
  Button,
  Card,
  CopyButton,
  Group,
  Image,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { unwrap } from '../api/client';
import { createShareCard, listShareCards, revokeShareCard } from '../api/gen';
import type { ShareCard, ShareMoment } from '../api/gen';
import { shortDate } from '../lib/text';
import { useOrg } from '../lib/session';
import { canShareNatively, cardUrl, intentUrl, shareNatively, type Target } from '../lib/share';

// Teilen: one good moment, as a card, behind a link the parent can take back.
//
// The defaults are the privacy-preserving ones. The card names nobody until
// the parent says otherwise, never shows the class, and the page it opens
// carries no photo, no task and nothing the child said — the server has no
// column that could hold them. The first name is allowed, with a plain warning
// for the public networks, where the parent's own surname stands beside it.

type Moment = ShareMoment['moment'];
type Name = 'none' | 'initial' | 'first';

const momentLabel: Record<Moment, string> = { badge: 'Abzeichen', streak: 'Serie', solved: 'Gelöst' };

export function ShareCardDialog({
  childId,
  childName,
  opened,
  onClose,
}: {
  childId: string;
  childName: string;
  opened: boolean;
  onClose: () => void;
}) {
  const org = useOrg();
  const qc = useQueryClient();
  const key = ['share-cards', childId];
  const list = useQuery({
    queryKey: key,
    queryFn: () => unwrap(listShareCards({ path: { childId }, headers: org })),
    enabled: opened,
  });

  const [moment, setMoment] = useState<Moment | null>(null);
  const [name, setName] = useState<Name>('none');
  const [subject, setSubject] = useState<string | null>(null);
  const [withReferral, setWithReferral] = useState(true);
  const [made, setMade] = useState<ShareCard | null>(null);

  const moments = list.data?.moments ?? [];
  useEffect(() => {
    if (!moment && moments.length > 0) setMoment(moments[0].moment);
  }, [moment, moments]);
  useEffect(() => {
    if (!opened) setMade(null);
  }, [opened]);

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        createShareCard({
          path: { childId },
          headers: org,
          body: { moment: moment!, name, subject: subject ?? undefined, withReferral },
        }),
      ),
    onSuccess: (card) => {
      setMade(card);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(revokeShareCard({ path: { id }, headers: org })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
    onError: (err) => notifications.show({ color: 'red', message: (err as Error).message }),
  });

  const chosen = moments.find((m) => m.moment === moment);

  return (
    <Modal opened={opened} onClose={onClose} title="Einen Moment teilen" size="lg">
      {list.isLoading ? <Loader size="sm" /> : null}

      {list.data && moments.length === 0 ? (
        <Text size="sm" c="dimmed">
          Noch nichts zu teilen. Eine Karte gibt es ab der ersten selbst gelösten Aufgabe, ab drei
          Tagen in Folge oder ab dem ersten Abzeichen nach „Erste Schritte“.
        </Text>
      ) : null}

      {made ? (
        <Made card={made} onAnother={() => setMade(null)} />
      ) : chosen ? (
        <Stack>
          <SegmentedControl
            fullWidth
            value={moment ?? undefined}
            onChange={(v) => setMoment(v as Moment)}
            data={moments.map((m) => ({ value: m.moment, label: momentLabel[m.moment] }))}
          />
          <Preview moment={chosen} name={shown(name, childName)} subject={subject} />

          <div>
            <Text size="sm" fw={600} mb={4}>
              Wie heißt dein Kind auf der Karte?
            </Text>
            <SegmentedControl
              fullWidth
              value={name}
              onChange={(v) => setName(v as Name)}
              data={[
                { value: 'none', label: 'Unser Kind' },
                { value: 'initial', label: 'Initiale' },
                { value: 'first', label: 'Vorname' },
              ]}
            />
            {name === 'first' ? (
              <Text size="xs" c="gray.8" fw={500} mt={6}>
                In einer Eltern-Gruppe unbedenklich. Auf LinkedIn oder X steht dein Name daneben –
                zusammen mit dem Vornamen ist dein Kind dann öffentlich auffindbar.
              </Text>
            ) : null}
          </div>

          {list.data && list.data.subjects.length > 0 ? (
            <Select
              label="Fach zeigen"
              placeholder="Kein Fach"
              clearable
              value={subject}
              onChange={setSubject}
              data={list.data.subjects}
            />
          ) : null}

          <Switch
            checked={withReferral}
            onChange={(e) => setWithReferral(e.currentTarget.checked)}
            label="Einladung anhängen"
            description="Wer über die Karte startet, testet einen Monat statt 14 Tage. Bucht die Familie, bekommt ihr einen Monat geschenkt."
          />

          <Text size="xs" c="dimmed">
            Nie auf der Karte: Fotos, Aufgaben, Antworten, Tipps, Uhrzeiten, der Lernbericht. Der Link
            gilt 30 Tage und lässt sich jederzeit zurücknehmen.
          </Text>

          <Button color="junior" radius="xl" loading={create.isPending} onClick={() => create.mutate()}>
            Karte erstellen
          </Button>
        </Stack>
      ) : null}

      {list.data && list.data.cards.length > 0 ? (
        <Stack gap="xs" mt="lg">
          <Text size="sm" fw={600}>
            Geteilte Links
          </Text>
          {list.data.cards.map((c) => (
            <Group key={c.id} justify="space-between" wrap="nowrap">
              <div style={{ minWidth: 0 }}>
                <Anchor href={cardUrl(c.token)} target="_blank" rel="noreferrer" size="sm" lineClamp={1}>
                  {c.title}
                </Anchor>
                <Text size="xs" c="dimmed">
                  gilt bis {shortDate(c.expiresAt)}
                </Text>
              </div>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                loading={revoke.isPending && revoke.variables === c.id}
                onClick={() => revoke.mutate(c.id)}
              >
                Zurücknehmen
              </Button>
            </Group>
          ))}
        </Stack>
      ) : null}
    </Modal>
  );
}

function shown(choice: Name, name: string): string {
  const trimmed = name.trim();
  if (choice === 'first') return trimmed.split(' ')[0] || 'Unser Kind';
  if (choice === 'initial') return trimmed ? `${trimmed[0].toUpperCase()}.` : 'Unser Kind';
  return 'Unser Kind';
}

function headline(m: ShareMoment, who: string): string {
  if (m.moment === 'badge') return `${who} hat trotzdem das Abzeichen „${m.badge}“ geschafft.`;
  if (m.moment === 'streak') return `${who} ist trotzdem ${m.figure} Tage in Folge drangeblieben.`;
  return `${who} hat trotzdem ${m.figure === 1 ? '1 Aufgabe' : `${m.figure} Aufgaben`} selbst gelöst.`;
}

// Preview draws the card the server will draw, before there is one to fetch.
// The server's sentence is the source of truth; once the card exists, the
// dialog shows the real image instead.
function Preview({ moment, name, subject }: { moment: ShareMoment; name: string; subject: string | null }) {
  return (
    <div
      style={{
        aspectRatio: '1200 / 630',
        background: '#d9480f',
        color: '#fffdf8',
        borderRadius: 12,
        padding: '5% 6.5%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        containerType: 'inline-size',
      }}
    >
      <Group justify="space-between">
        <span style={{ fontWeight: 700, fontSize: '3.4cqw' }}>Sokrates</span>
        {subject ? (
          <span style={{ fontWeight: 700, fontSize: '2cqw', color: '#ffd8a8', textTransform: 'uppercase' }}>
            {subject}
          </span>
        ) : null}
      </Group>
      <div>
        <div style={{ fontSize: '3.3cqw', color: '#fbd9c9' }}>Sokrates hat keine einzige Lösung verraten.</div>
        <div style={{ fontSize: '5.6cqw', fontWeight: 700, lineHeight: 1.12, marginTop: '1.5cqw' }}>
          {headline(moment, name)}
        </div>
      </div>
      <div style={{ fontSize: '2.2cqw', color: '#ffd8a8' }}>
        Weil Kinder nicht die Antwort brauchen, sondern die nächste gute Frage.
      </div>
    </div>
  );
}

function Made({ card, onAnother }: { card: ShareCard; onAnother: () => void }) {
  const url = cardUrl(card.token);
  const text = `${card.title} Sokrates hat keine einzige Lösung verraten.`;
  const open = (target: Target) => window.open(intentUrl(target, text, url), '_blank', 'noopener');
  return (
    <Stack>
      <Image src={`/k/${card.token}/card.png`} radius="md" alt={card.title} />
      {canShareNatively ? (
        <Button color="junior" radius="xl" onClick={() => void shareNatively('Sokrates', text, url)}>
          Teilen …
        </Button>
      ) : null}
      <Group grow>
        <Button variant="light" color="green" onClick={() => open('whatsapp')}>
          WhatsApp
        </Button>
        <Button variant="light" onClick={() => open('linkedin')}>
          LinkedIn
        </Button>
        <Button variant="light" color="dark" onClick={() => open('x')}>
          X
        </Button>
      </Group>
      <CopyButton value={url}>
        {({ copied, copy }) => (
          <Button variant="default" onClick={copy}>
            {copied ? 'Link kopiert' : 'Link kopieren'}
          </Button>
        )}
      </CopyButton>
      <Card withBorder padding="sm">
        <Text size="xs" c="dimmed">
          {url}
        </Text>
      </Card>
      {card.shownName && card.shownName.length > 2 ? (
        <Alert>
          Auf der Karte steht „{card.shownName}“. Für öffentliche Netzwerke lieber „Unser Kind“ wählen.
        </Alert>
      ) : null}
      <Button variant="subtle" onClick={onAnother}>
        Noch eine Karte
      </Button>
    </Stack>
  );
}
